import {
  Component,
  Input,
  inject,
  WritableSignal,
  signal,
  computed,
  input,
  InputSignal,
  ViewChild,
  Injector,
  effect,
} from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TableRow } from "@bitwarden/web-vault/app/components/tables/transaction-table/transaction-table";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { TransactionOrigin } from "@bitwarden/web-vault/app/models/types/transaction.types";
import { Button } from "@bitwarden/web-vault/app/components/buttons/gloss-button/button";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { actionButtonOptions } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/accounts-add-edit/component.options";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { DeleteItem, deletingItem } from "@bitwarden/web-vault/app/components/tables/deleteItem";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { AddEditTransactionFormComponent } from "@bitwarden/web-vault/app/components/add-edit-transaction-form/add-edit-transaction-form.component";
import { TransactionDataHelper } from "@bitwarden/web-vault/app/shared/utils/helper.transactions/transaction-data-helper";
import { transactionDataType } from "@bitwarden/web-vault/app/models/types/transactionData.types";
import { TransactionForm } from "@bitwarden/web-vault/app/models/types/account.types";
import { isStockOrOther } from "@bitwarden/web-vault/app/models/view/account/account.view";

export const editTransactionTab: Record<string, string> = {
  transactionDetails: "Transaction Details",
  categories: "Categories",
};

@Component({
  selector: "edit-transaction-details",
  templateUrl: "./edit-transaction-details.component.html",
})
export class EditTransactionDetailsComponent {
  @Input() accountId: string;
  rowData: InputSignal<TableRow> = input(null);
  tab: InputSignal<string> = input("");
  protected tabActive: WritableSignal<string> = signal(editTransactionTab.transactionDetails);
  @Input() selectedDateRange: DateRange<any>;
  @Input() isAppendRatesRange: boolean;

  @ViewChild(AddEditTransactionFormComponent)
  addEditTransactionFormComponent!: AddEditTransactionFormComponent;

  protected readonly editTransactionTab = editTransactionTab;

  isSymbolAndCurrencyEqual: boolean = false;

  direction: TransactionDirection;
  symbol: string;
  accounts: Array<object>;
  saveButton: Button;
  /** True while the transaction is being saved; the panel won't close on click-away/Escape. */
  protected submitting = false;
  protected isEditTransaction: boolean = false;

  protected transactionToEdit: transactionDataType;

  private deleteItem = new DeleteItem(deletingItem.transaction);
  protected i18nService: I18nService = inject(I18nService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);

  private transactionDataHelper: TransactionDataHelper;

  readonly origins: Record<string, TransactionOrigin> = {
    manual: "manual",
    basiq: "basiq",
    plaid: "plaid",
  };

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<EditTransactionDetailsComponent>,
    public injector: Injector,
  ) {
    this.saveButton = new ActionButton({
      ...actionButtonOptions,
      text: this.i18nService.t("save"),
      onClick: this.submit.bind(this),
    });
    this.transactionDataHelper = new TransactionDataHelper(this.injector);

    const tabInitialStateEffect = effect(
      () => {
        if (this.tab()) {
          this.tabActive.set(this.tab());
        }
        tabInitialStateEffect.destroy();
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      this.transactionToEdit = {
        account: this.rowData().accountView,
        formData: this.convertTransactionDetailToTransactionForm(this.rowData()),
      };

      if (this.origin() === this.origins.manual) {
        this.isEditTransaction = true;
      }
    });
  }

  protected origin = computed(() => {
    return this.origins[this.rowData().transactionView.origin];
  });

  async delete(): Promise<void> {
    this.deleteItem
      .delete(this.rowData().transactionView)
      .then((res) => res && this.dialogRef?.close());
  }

  onSymbolAndCurrencyEqualChange(isEqual: boolean) {
    this.isSymbolAndCurrencyEqual = isEqual;
  }

  private async submit() {
    if (!this.rowData()) {
      return;
    }

    const result = this.addEditTransactionFormComponent.completeForm();
    if (result && result !== "touched") {
      this.submitting = true;
      this.transactionDataHelper
        .receivedTransactionData(result, true, this.rowData().transactionView)
        .then((r) => {
          this.receivedSuccessfulSubmit();
        })
        .finally(() => (this.submitting = false));
    }

    this.saveButton = new ActionButton({
      ...actionButtonOptions,
      text: this.i18nService.t("save"),
      onClick: this.submit.bind(this),
    });
  }

  receivedSuccessfulSubmit() {
    this.addEditTransactionFormComponent.resetValue();
    this.notifyService.notify(NotificationEnum.success, "succeeded", "updatedSuccessfully");
    this.dialogRef.close();
  }

  convertTransactionDetailToTransactionForm(rowData: TableRow): TransactionForm {
    const selectedAccountType = rowData.accountView?.type;
    // True for the "unit-priced" account types (stock/other), where a transaction carries
    // a unit price + a unit count; false for everything else — cash accounts (bank/credit)
    // whose amount is a plain money value, and the aggregator types that also inhabit
    // AccountType. See the TransactionForm field docs for what `amount`/`quantity` hold.
    const isUnitPricedAccount = isStockOrOther(selectedAccountType);
    return {
      amount: isUnitPricedAccount
        ? rowData.transactionView.valuationPrice
        : rowData.transactionView.quantity.actualQuantity.amount,
      quantity: isUnitPricedAccount
        ? rowData.transactionView.quantity.actualQuantity.amount
        : rowData.transactionView.valuationPrice,
      symbol: rowData.transactionView.quantity.actualQuantity.symbol,
      // #4443: seed the currency from the transaction, not the account. A bank/credit
      // transaction can hold a symbol that differs from its account currency (e.g. a USD
      // transaction in an AUD account via Fiskil); seeding from the account rewrote the
      // symbol on any edit. Fall back to the account only when the transaction has none.
      currency: rowData.transactionView.quantity.currency || rowData.accountView?.currency,
      categoryId: rowData.transactionView.c_categories[0],
      date: rowData.transactionView.glossPostDate.dateString,
      transactionStatus: rowData.transactionView.definition,
      description: rowData.description,
      type: rowData.transactionView.direction,
    };
  }

  protected readonly ViewEnum = View;
}
